import type {
  IGhIconName,
  IGhSuggestion,
  IGhWelcome,
} from '@/gpthelp/gpthelp-host-contract';

/** What the member may do here, as the welcome needs it (from the tool gate). */
export interface IAssistantWelcomeGate {
  /** The ops the assistant may prepare (empty while writes are off). */
  assistantOps: string[];
  canManageCatalog: boolean;
  canViewStats: boolean;
  multipleMenus: boolean;
}

/** A suggestion chip before translation: its i18n key and icon. */
export interface IAssistantSuggestionSpec {
  key: string;
  icon?: IGhIconName;
}

/** The welcome shows at most this many suggestions (host contract §3.4). */
export const ASSISTANT_MAX_SUGGESTIONS = 4;

/** A chip, or `false` when the member may not use it. */
type TChip = IAssistantSuggestionSpec | false;

function chip(name: string, icon?: IGhIconName): IAssistantSuggestionSpec {
  const key = `assistant.suggest.${name}`;
  return icon ? { key, icon } : { key };
}

function isMenuPage(pageKey: string): boolean {
  return pageKey === 'menu' || pageKey.startsWith('menu-');
}

function pageChips(
  pageKey: string | null,
  gate: IAssistantWelcomeGate
): TChip[] {
  if (pageKey && isMenuPage(pageKey))
    return [
      gate.assistantOps.includes('adjustPrices') &&
        chip('raiseDrinks', 'trendingUp'),
      gate.assistantOps.includes('setAvailability') && chip('soldOut', 'block'),
      gate.canManageCatalog &&
        !gate.multipleMenus &&
        chip('fromPhoto', 'camera'),
      chip('noPhoto'),
    ];
  if (pageKey === 'dashboard')
    return [
      gate.canViewStats && chip('salesYesterday'),
      gate.canViewStats && chip('topWeek'),
      gate.canViewStats && chip('compareWeek'),
    ];
  if (pageKey?.startsWith('stats-')) return [chip('explain'), chip('bestDay')];
  if (pageKey === 'sales-orders')
    return [chip('findOrder'), chip('takeawayYesterday')];
  return otherChips(gate);
}

/** Any other page: today's sales and «Llevarme a una página». */
function otherChips(gate: IAssistantWelcomeGate): TChip[] {
  return [gate.canViewStats && chip('salesToday'), chip('goToPage')];
}

function allowed(chips: TChip[]): IAssistantSuggestionSpec[] {
  return chips.filter((entry): entry is IAssistantSuggestionSpec => !!entry);
}

/**
 * The suggestion chips of a page (plan §9.5), filtered by what the member
 * may do; a page none of whose chips the member may use gets the general
 * ones, so the welcome always offers a start. Page keys are the drawer keys
 * (`activeNavKey`); null is a page outside the drawer.
 */
export function assistantSuggestions(
  pageKey: string | null,
  gate: IAssistantWelcomeGate
): IAssistantSuggestionSpec[] {
  const chips = allowed(pageChips(pageKey, gate));
  return (chips.length ? chips : allowed(otherChips(gate))).slice(
    0,
    ASSISTANT_MAX_SUGGESTIONS
  );
}

/**
 * The welcome block (plan §9.5): what the assistant can do (changes, or
 * answers only while writes are off), what stays with the member, and the
 * page's suggestions. The suggestions title is left out when none apply.
 */
export function assistantWelcome(
  pageKey: string | null,
  gate: IAssistantWelcomeGate,
  t: (key: string) => string
): IGhWelcome {
  const suggestions = assistantSuggestions(pageKey, gate).map(
    (spec): IGhSuggestion =>
      spec.icon ? { text: t(spec.key), icon: spec.icon } : { text: t(spec.key) }
  );
  const welcome: IGhWelcome = {
    title: t('assistant.welcome.title'),
    paragraphs: [
      t(
        gate.assistantOps.length
          ? 'assistant.welcome.bodyWrites'
          : 'assistant.welcome.bodyRead'
      ),
    ],
    note: t('assistant.welcome.limits'),
    disclaimer: t('assistant.welcome.fineprint'),
    suggestions,
    placeholder: t('assistant.welcome.placeholder'),
  };
  if (suggestions.length)
    welcome.suggestionsTitle = t('assistant.welcome.suggestionsLabel');
  return welcome;
}
